/** @module @liquidefy/code-editor/language/language-navigation @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS — types and engine dependencies ═══

import type {
  LocationLink,
  DocumentSymbol,
  SymbolKind,
  CallHierarchyItem,
  TypeHierarchyItem,
  BreadcrumbItem,
  RenameResult,
  RenamePreview,
  RenameFileChange,
  EditOperation,
  Position,
  Range,
  LanguageId,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  comparePositions,
  rangeContains,
  rangesOverlap,
  positionToOffset,
  offsetToPosition,
  getWordAtPosition,
} from '../code-editor.js';

// ═══ §2 DEFINITION NAVIGATION — go-to definition, declaration, implementation, type ═══

/**
 * Find the definition location(s) for a symbol name at the given position.
 * Searches through the symbol index for matching declarations.
 */
export function findDefinition(
  symbols: readonly DocumentSymbol[],
  name: string,
  position: Position,
  content: string,
): LocationLink[] {
  const results: LocationLink[] = [];
  const originRange = computeWordRange(content, position);

  function searchSymbols(syms: readonly DocumentSymbol[], uri: string): void {
    for (const sym of syms) {
      if (sym.name === name && isDefinitionKind(sym.kind)) {
        results.push({
          originSelectionRange: originRange ?? undefined,
          targetUri: uri,
          targetRange: sym.range,
          targetSelectionRange: sym.selectionRange,
        });
      }
      if (sym.children) {
        searchSymbols(sym.children, uri);
      }
    }
  }

  searchSymbols(symbols, 'current');
  return results;
}

/**
 * Find the declaration of a symbol by name.
 * Declarations are typically the first occurrence in a higher scope.
 */
export function findDeclaration(
  symbols: readonly DocumentSymbol[],
  name: string,
): LocationLink[] {
  const results: LocationLink[] = [];

  function search(syms: readonly DocumentSymbol[]): void {
    for (const sym of syms) {
      if (sym.name === name) {
        results.push({
          targetUri: 'current',
          targetRange: sym.range,
          targetSelectionRange: sym.selectionRange,
        });
      }
      if (sym.children) {
        search(sym.children);
      }
    }
  }

  search(symbols);
  return results;
}

/**
 * Find implementations of an interface, abstract class, or abstract method.
 */
export function findImplementation(
  symbols: readonly DocumentSymbol[],
  name: string,
): LocationLink[] {
  const results: LocationLink[] = [];

  function search(syms: readonly DocumentSymbol[]): void {
    for (const sym of syms) {
      if (sym.name === name && isImplementationKind(sym.kind)) {
        results.push({
          targetUri: 'current',
          targetRange: sym.range,
          targetSelectionRange: sym.selectionRange,
        });
      }
      if (sym.children) {
        search(sym.children);
      }
    }
  }

  search(symbols);
  return results;
}

/**
 * Find the type definition for a symbol (e.g. the interface or class type
 * of a variable).
 */
export function findTypeDefinition(
  symbols: readonly DocumentSymbol[],
  name: string,
): LocationLink[] {
  const results: LocationLink[] = [];

  function search(syms: readonly DocumentSymbol[]): void {
    for (const sym of syms) {
      if (sym.name === name && isTypeKind(sym.kind)) {
        results.push({
          targetUri: 'current',
          targetRange: sym.range,
          targetSelectionRange: sym.selectionRange,
        });
      }
      if (sym.children) {
        search(sym.children);
      }
    }
  }

  search(symbols);
  return results;
}

// ═══ §3 REFERENCE FINDING — occurrences and related symbols ═══

/**
 * Find all references to a name in the content.
 * Optionally includes the declaration itself.
 */
export function findReferences(
  content: string,
  name: string,
  includeDeclaration?: boolean,
): LocationLink[] {
  const results: LocationLink[] = [];
  const wordRegex = new RegExp(`\\b${escapeRegExp(name)}\\b`, 'g');
  let match: RegExpExecArray | null;
  let isFirst = true;

  while ((match = wordRegex.exec(content)) !== null) {
    if (isFirst && !(includeDeclaration ?? true)) {
      isFirst = false;
      continue;
    }
    isFirst = false;

    const start = offsetToPosition(content, match.index);
    const end = offsetToPosition(content, match.index + match[0].length);

    results.push({
      targetUri: 'current',
      targetRange: { start, end },
      targetSelectionRange: { start, end },
    });
  }

  return results;
}

/**
 * Find all occurrences of a word in the content, regardless of scope.
 */
export function findAllOccurrences(
  content: string,
  word: string,
): LocationLink[] {
  const results: LocationLink[] = [];
  const regex = new RegExp(`\\b${escapeRegExp(word)}\\b`, 'g');
  let match: RegExpExecArray | null;

  while ((match = regex.exec(content)) !== null) {
    const start = offsetToPosition(content, match.index);
    const end = offsetToPosition(content, match.index + match[0].length);

    results.push({
      targetUri: 'current',
      targetRange: { start, end },
      targetSelectionRange: { start, end },
    });
  }

  return results;
}

/**
 * Highlight related symbols at the cursor position.
 * Returns ranges for all symbols that are semantically related to the
 * symbol under the cursor.
 */
export function highlightRelated(
  content: string,
  position: Position,
): Range[] {
  const wordInfo = getWordAtPosition(content, position);
  if (wordInfo === null) return [];

  const word = wordInfo.word;
  const ranges: Range[] = [];
  const regex = new RegExp(`\\b${escapeRegExp(word)}\\b`, 'g');
  let match: RegExpExecArray | null;

  while ((match = regex.exec(content)) !== null) {
    const start = offsetToPosition(content, match.index);
    const end = offsetToPosition(content, match.index + match[0].length);
    ranges.push({ start, end });
  }

  return ranges;
}

// ═══ §4 SYMBOL NAVIGATION — document symbols, breadcrumbs, traversal ═══

/**
 * Extract document symbols from content for a given language.
 * Performs regex-based symbol detection for common patterns.
 */
export function getDocumentSymbols(
  content: string,
  language: LanguageId,
): DocumentSymbol[] {
  const symbols: DocumentSymbol[] = [];
  const langId = language as string;
  const lines = content.split('\n');

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    const trimmed = line.trim();

    // Function/method detection
    const funcPatterns = getFunctionPatterns(langId);
    for (const pattern of funcPatterns) {
      const match = trimmed.match(pattern.regex);
      if (match && match[1]) {
        const name = match[1];
        const col = line.indexOf(name);
        const endLine = findBlockEnd(lines, i, langId);
        symbols.push({
          name,
          kind: pattern.kind,
          range: { start: { line: i, column: 0 }, end: { line: endLine, column: lines[endLine]?.length ?? 0 } },
          selectionRange: { start: { line: i, column: col }, end: { line: i, column: col + name.length } },
        });
      }
    }

    // Class/interface/enum detection
    const typePatterns = getTypePatterns(langId);
    for (const pattern of typePatterns) {
      const match = trimmed.match(pattern.regex);
      if (match && match[1]) {
        const name = match[1];
        const col = line.indexOf(name);
        const endLine = findBlockEnd(lines, i, langId);
        symbols.push({
          name,
          kind: pattern.kind,
          range: { start: { line: i, column: 0 }, end: { line: endLine, column: lines[endLine]?.length ?? 0 } },
          selectionRange: { start: { line: i, column: col }, end: { line: i, column: col + name.length } },
        });
      }
    }

    // Variable/constant detection
    const varPatterns = getVariablePatterns(langId);
    for (const pattern of varPatterns) {
      const match = trimmed.match(pattern.regex);
      if (match && match[1]) {
        const name = match[1];
        const col = line.indexOf(name);
        symbols.push({
          name,
          kind: pattern.kind,
          range: { start: { line: i, column: 0 }, end: { line: i, column: line.length } },
          selectionRange: { start: { line: i, column: col }, end: { line: i, column: col + name.length } },
        });
      }
    }
  }

  return symbols;
}

/**
 * Search workspace symbols by a query pattern (fuzzy).
 */
export function getWorkspaceSymbols(
  symbolIndex: ReadonlyMap<string, DocumentSymbol[]>,
  query: string,
): DocumentSymbol[] {
  const results: DocumentSymbol[] = [];
  const queryLower = query.toLowerCase();

  for (const [_uri, symbols] of symbolIndex) {
    for (const sym of symbols) {
      if (sym.name.toLowerCase().includes(queryLower)) {
        results.push(sym);
      }
      if (sym.children) {
        for (const child of sym.children) {
          if (child.name.toLowerCase().includes(queryLower)) {
            results.push(child);
          }
        }
      }
    }
  }

  return results.sort((a, b) => {
    const aStart = a.name.toLowerCase().indexOf(queryLower);
    const bStart = b.name.toLowerCase().indexOf(queryLower);
    return aStart - bStart;
  });
}

/**
 * Build a breadcrumb trail from the root to the symbol at the given position.
 */
export function buildBreadcrumbs(
  symbols: readonly DocumentSymbol[],
  position: Position,
): BreadcrumbItem[] {
  const crumbs: BreadcrumbItem[] = [];

  function walk(syms: readonly DocumentSymbol[]): boolean {
    for (const sym of syms) {
      if (rangeContains(sym.range, position)) {
        crumbs.push({
          name: sym.name,
          kind: sym.kind,
          range: sym.range,
        });
        if (sym.children) {
          walk(sym.children);
        }
        return true;
      }
    }
    return false;
  }

  walk(symbols);
  return crumbs;
}

/**
 * Find the symbol at a given position.
 */
export function symbolAtPosition(
  symbols: readonly DocumentSymbol[],
  position: Position,
): DocumentSymbol | null {
  let best: DocumentSymbol | null = null;

  function walk(syms: readonly DocumentSymbol[]): void {
    for (const sym of syms) {
      if (rangeContains(sym.range, position)) {
        best = sym;
        if (sym.children) {
          walk(sym.children);
        }
      }
    }
  }

  walk(symbols);
  return best;
}

/**
 * Find the parent symbol containing the given position.
 */
export function parentSymbol(
  symbols: readonly DocumentSymbol[],
  position: Position,
): DocumentSymbol | null {
  let parent: DocumentSymbol | null = null;

  function walk(syms: readonly DocumentSymbol[], currentParent: DocumentSymbol | null): void {
    for (const sym of syms) {
      if (rangeContains(sym.range, position)) {
        parent = currentParent;
        if (sym.children) {
          walk(sym.children, sym);
        }
      }
    }
  }

  walk(symbols, null);
  return parent;
}

/**
 * Find the next symbol after the given position (by start line).
 */
export function nextSymbol(
  symbols: readonly DocumentSymbol[],
  position: Position,
): DocumentSymbol | null {
  const flat = flattenSymbols(symbols);
  const sorted = flat.sort((a, b) => comparePositions(a.range.start, b.range.start));

  for (const sym of sorted) {
    if (comparePositions(sym.range.start, position) > 0) {
      return sym;
    }
  }

  return null;
}

/**
 * Find the previous symbol before the given position (by start line).
 */
export function previousSymbol(
  symbols: readonly DocumentSymbol[],
  position: Position,
): DocumentSymbol | null {
  const flat = flattenSymbols(symbols);
  const sorted = flat.sort((a, b) => comparePositions(b.range.start, a.range.start));

  for (const sym of sorted) {
    if (comparePositions(sym.range.start, position) < 0) {
      return sym;
    }
  }

  return null;
}

// ═══ §5 CALL & TYPE HIERARCHY — incoming/outgoing calls, supertypes/subtypes ═══

/**
 * Build a call hierarchy item from a symbol.
 */
export function buildCallHierarchy(
  symbols: readonly DocumentSymbol[],
  target: string,
): CallHierarchyItem | null {
  const sym = findSymbolByName(symbols, target);
  if (sym === null) return null;

  return {
    name: sym.name,
    kind: sym.kind,
    uri: 'current',
    range: sym.range,
    selectionRange: sym.selectionRange,
    detail: sym.detail,
  };
}

/**
 * Find incoming calls to a call hierarchy item.
 * Searches for references to the item's name in function bodies.
 */
export function getIncomingCalls(
  symbols: readonly DocumentSymbol[],
  item: CallHierarchyItem,
): CallHierarchyItem[] {
  const callers: CallHierarchyItem[] = [];
  const flat = flattenSymbols(symbols);

  for (const sym of flat) {
    if (sym.name === item.name) continue;
    if (isCallableKind(sym.kind)) {
      callers.push({
        name: sym.name,
        kind: sym.kind,
        uri: 'current',
        range: sym.range,
        selectionRange: sym.selectionRange,
        detail: sym.detail,
      });
    }
  }

  return callers;
}

/**
 * Find outgoing calls from a call hierarchy item.
 * Searches for function call patterns within the item's range.
 */
export function getOutgoingCalls(
  symbols: readonly DocumentSymbol[],
  item: CallHierarchyItem,
): CallHierarchyItem[] {
  const callees: CallHierarchyItem[] = [];
  const flat = flattenSymbols(symbols);

  for (const sym of flat) {
    if (sym.name === item.name) continue;
    if (isCallableKind(sym.kind) && rangesOverlap(item.range, sym.range)) {
      callees.push({
        name: sym.name,
        kind: sym.kind,
        uri: 'current',
        range: sym.range,
        selectionRange: sym.selectionRange,
        detail: sym.detail,
      });
    }
  }

  return callees;
}

/**
 * Build a type hierarchy item from a symbol.
 */
export function buildTypeHierarchy(
  symbols: readonly DocumentSymbol[],
  target: string,
): TypeHierarchyItem | null {
  const sym = findSymbolByName(symbols, target);
  if (sym === null || !isTypeKind(sym.kind)) return null;

  return {
    name: sym.name,
    kind: sym.kind,
    uri: 'current',
    range: sym.range,
    selectionRange: sym.selectionRange,
    detail: sym.detail,
  };
}

/**
 * Find supertypes (base classes, implemented interfaces) of a type.
 */
export function getSupertypes(
  symbols: readonly DocumentSymbol[],
  item: TypeHierarchyItem,
): TypeHierarchyItem[] {
  const supers: TypeHierarchyItem[] = [];
  const flat = flattenSymbols(symbols);

  for (const sym of flat) {
    if (sym.name === item.name) continue;
    if (isTypeKind(sym.kind) && sym.detail?.includes(item.name)) {
      supers.push({
        name: sym.name,
        kind: sym.kind,
        uri: 'current',
        range: sym.range,
        selectionRange: sym.selectionRange,
        detail: sym.detail,
      });
    }
  }

  return supers;
}

/**
 * Find subtypes (derived classes, implementors) of a type.
 */
export function getSubtypes(
  symbols: readonly DocumentSymbol[],
  item: TypeHierarchyItem,
): TypeHierarchyItem[] {
  const subs: TypeHierarchyItem[] = [];
  const flat = flattenSymbols(symbols);

  for (const sym of flat) {
    if (sym.name === item.name) continue;
    if (isTypeKind(sym.kind) && item.detail?.includes(sym.name)) {
      subs.push({
        name: sym.name,
        kind: sym.kind,
        uri: 'current',
        range: sym.range,
        selectionRange: sym.selectionRange,
        detail: sym.detail,
      });
    }
  }

  return subs;
}

// ═══ §6 RENAME — prepare, compute edits, validate, preview ═══

/**
 * Prepare a rename operation at the given position.
 * Returns the range and placeholder text, or null if renaming is not possible.
 */
export function prepareRename(
  content: string,
  position: Position,
  symbols: readonly DocumentSymbol[],
): { range: Range; placeholder: string } | null {
  const wordInfo = getWordAtPosition(content, position);
  if (wordInfo === null) return null;

  if (!isRenameable(symbols, position)) return null;

  return {
    range: wordInfo.range,
    placeholder: wordInfo.word,
  };
}

/**
 * Compute all edits needed to rename a symbol at the given position.
 */
export function computeRenameEdits(
  content: string,
  position: Position,
  newName: string,
  symbols: readonly DocumentSymbol[],
): EditorResult<RenameResult> {
  const wordInfo = getWordAtPosition(content, position);
  if (wordInfo === null) {
    return err('NO_SYMBOL', 'No symbol found at position');
  }

  const oldName = wordInfo.word;
  const validateResult = validateRename(newName, 'typescript' as unknown as LanguageId);
  if (!validateResult.success) {
    return validateResult as EditorResult<RenameResult>;
  }

  const occurrences = findAllOccurrences(content, oldName);
  const edits: EditOperation[] = [];

  for (const occ of occurrences) {
    edits.push({
      kind: 'replace',
      range: occ.targetRange,
      text: newName,
      origin: 'external',
    });
  }

  const changes = new Map<string, EditOperation[]>();
  changes.set('current', edits);

  return ok(
    { changes, documentChanges: [] },
    'RENAME_COMPUTED',
    `Rename '${oldName}' to '${newName}': ${edits.length} occurrences`,
  );
}

/**
 * Validate that a new name is valid for a given language.
 */
export function validateRename(
  newName: string,
  _language: LanguageId,
): EditorResult<boolean> {
  if (newName.length === 0) {
    return err('EMPTY_NAME', 'New name cannot be empty');
  }

  if (!/^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(newName)) {
    return err('INVALID_NAME', `'${newName}' is not a valid identifier`);
  }

  const reserved = [
    'break', 'case', 'catch', 'continue', 'debugger', 'default', 'delete',
    'do', 'else', 'finally', 'for', 'function', 'if', 'in', 'instanceof',
    'new', 'return', 'switch', 'this', 'throw', 'try', 'typeof', 'var',
    'void', 'while', 'with', 'class', 'const', 'enum', 'export', 'extends',
    'import', 'super', 'implements', 'interface', 'let', 'package', 'private',
    'protected', 'public', 'static', 'yield',
  ];

  if (reserved.includes(newName)) {
    return err('RESERVED_WORD', `'${newName}' is a reserved keyword`);
  }

  return ok(true, 'VALID', 'Name is valid');
}

/**
 * Preview the result of a rename without applying it.
 */
export function previewRename(
  content: string,
  position: Position,
  newName: string,
  symbols: readonly DocumentSymbol[],
): EditorResult<RenamePreview> {
  const wordInfo = getWordAtPosition(content, position);
  if (wordInfo === null) {
    return err('NO_SYMBOL', 'No symbol found at position');
  }

  const oldName = wordInfo.word;
  const occurrences = findAllOccurrences(content, oldName);

  const changes: RenameFileChange[] = occurrences.map((occ) => ({
    uri: 'current',
    oldText: oldName,
    newText: newName,
    range: occ.targetRange,
  }));

  return ok(
    { changes, conflictCount: 0 },
    'PREVIEW_COMPUTED',
    `Rename '${oldName}' to '${newName}': ${changes.length} changes`,
  );
}

/**
 * Check whether the symbol at the given position can be renamed.
 */
export function isRenameable(
  symbols: readonly DocumentSymbol[],
  position: Position,
): boolean {
  const sym = symbolAtPosition(symbols, position);
  if (sym === null) return true; // Allow renaming even without symbol info

  // Keywords and built-in types cannot be renamed
  const nonRenameable: SymbolKind[] = ['key', 'null', 'boolean', 'number', 'string'];
  return !nonRenameable.includes(sym.kind);
}

// ═══ INTERNAL HELPERS ═══

function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function computeWordRange(content: string, position: Position): Range | null {
  const wordInfo = getWordAtPosition(content, position);
  if (wordInfo === null) return null;
  return wordInfo.range;
}

function isDefinitionKind(kind: SymbolKind): boolean {
  return ['function', 'method', 'class', 'interface', 'enum', 'variable', 'constant', 'property', 'field', 'constructor', 'struct', 'type-parameter', 'namespace', 'module'].includes(kind);
}

function isImplementationKind(kind: SymbolKind): boolean {
  return ['class', 'method', 'function', 'struct'].includes(kind);
}

function isTypeKind(kind: SymbolKind): boolean {
  return ['class', 'interface', 'enum', 'struct', 'type-parameter'].includes(kind);
}

function isCallableKind(kind: SymbolKind): boolean {
  return ['function', 'method', 'constructor'].includes(kind);
}

function flattenSymbols(symbols: readonly DocumentSymbol[]): DocumentSymbol[] {
  const result: DocumentSymbol[] = [];
  function walk(syms: readonly DocumentSymbol[]): void {
    for (const sym of syms) {
      result.push(sym);
      if (sym.children) {
        walk(sym.children);
      }
    }
  }
  walk(symbols);
  return result;
}

function findSymbolByName(symbols: readonly DocumentSymbol[], name: string): DocumentSymbol | null {
  const flat = flattenSymbols(symbols);
  return flat.find((s) => s.name === name) ?? null;
}

interface SymbolPattern {
  readonly regex: RegExp;
  readonly kind: SymbolKind;
}

function getFunctionPatterns(langId: string): SymbolPattern[] {
  switch (langId) {
    case 'typescript':
    case 'javascript':
    case 'jsx':
    case 'tsx':
      return [
        { regex: /^(?:export\s+)?(?:async\s+)?function\s+(\w+)/, kind: 'function' },
        { regex: /^(?:export\s+)?(?:const|let|var)\s+(\w+)\s*=\s*(?:async\s+)?(?:\([^)]*\)|[^=])\s*=>/, kind: 'function' },
        { regex: /^\s*(?:public|private|protected|static|async|abstract)*\s*(\w+)\s*\(/, kind: 'method' },
      ];
    case 'python':
      return [
        { regex: /^(?:async\s+)?def\s+(\w+)/, kind: 'function' },
      ];
    case 'rust':
      return [
        { regex: /^(?:pub(?:\(crate\))?\s+)?(?:async\s+)?fn\s+(\w+)/, kind: 'function' },
      ];
    case 'go':
      return [
        { regex: /^func\s+(?:\([^)]+\)\s+)?(\w+)/, kind: 'function' },
      ];
    case 'java':
    case 'csharp':
    case 'kotlin':
    case 'scala':
      return [
        { regex: /(?:public|private|protected|static|abstract|final|override|virtual)*\s*\w+\s+(\w+)\s*\(/, kind: 'method' },
      ];
    default:
      return [
        { regex: /function\s+(\w+)/, kind: 'function' },
        { regex: /def\s+(\w+)/, kind: 'function' },
        { regex: /fn\s+(\w+)/, kind: 'function' },
      ];
  }
}

function getTypePatterns(langId: string): SymbolPattern[] {
  switch (langId) {
    case 'typescript':
    case 'javascript':
    case 'jsx':
    case 'tsx':
      return [
        { regex: /^(?:export\s+)?(?:abstract\s+)?class\s+(\w+)/, kind: 'class' },
        { regex: /^(?:export\s+)?interface\s+(\w+)/, kind: 'interface' },
        { regex: /^(?:export\s+)?enum\s+(\w+)/, kind: 'enum' },
        { regex: /^(?:export\s+)?type\s+(\w+)/, kind: 'interface' },
      ];
    case 'python':
      return [
        { regex: /^class\s+(\w+)/, kind: 'class' },
      ];
    case 'rust':
      return [
        { regex: /^(?:pub(?:\(crate\))?\s+)?struct\s+(\w+)/, kind: 'struct' },
        { regex: /^(?:pub(?:\(crate\))?\s+)?enum\s+(\w+)/, kind: 'enum' },
        { regex: /^(?:pub(?:\(crate\))?\s+)?trait\s+(\w+)/, kind: 'interface' },
      ];
    case 'go':
      return [
        { regex: /^type\s+(\w+)\s+struct/, kind: 'struct' },
        { regex: /^type\s+(\w+)\s+interface/, kind: 'interface' },
      ];
    case 'java':
    case 'csharp':
    case 'kotlin':
      return [
        { regex: /(?:public|private|protected|abstract|static|final|sealed|data)*\s*class\s+(\w+)/, kind: 'class' },
        { regex: /(?:public|private|protected)*\s*interface\s+(\w+)/, kind: 'interface' },
        { regex: /(?:public|private|protected)*\s*enum\s+(\w+)/, kind: 'enum' },
      ];
    default:
      return [
        { regex: /class\s+(\w+)/, kind: 'class' },
        { regex: /interface\s+(\w+)/, kind: 'interface' },
        { regex: /enum\s+(\w+)/, kind: 'enum' },
      ];
  }
}

function getVariablePatterns(langId: string): SymbolPattern[] {
  switch (langId) {
    case 'typescript':
    case 'javascript':
    case 'jsx':
    case 'tsx':
      return [
        { regex: /^(?:export\s+)?const\s+(\w+)\s*[=:]/, kind: 'constant' },
        { regex: /^(?:export\s+)?let\s+(\w+)\s*[=:]/, kind: 'variable' },
        { regex: /^(?:export\s+)?var\s+(\w+)\s*[=:]/, kind: 'variable' },
      ];
    case 'python':
      return [
        { regex: /^([A-Z_][A-Z0-9_]*)\s*=/, kind: 'constant' },
      ];
    case 'rust':
      return [
        { regex: /^(?:pub(?:\(crate\))?\s+)?const\s+(\w+)/, kind: 'constant' },
        { regex: /^(?:pub(?:\(crate\))?\s+)?static\s+(\w+)/, kind: 'variable' },
      ];
    case 'go':
      return [
        { regex: /^(?:var|const)\s+(\w+)/, kind: 'variable' },
      ];
    default:
      return [];
  }
}

function findBlockEnd(lines: readonly string[], startLine: number, langId: string): number {
  const indentBased = ['python', 'yaml'].includes(langId);

  if (indentBased) {
    const startIndent = getIndent(lines[startLine] ?? '');
    for (let i = startLine + 1; i < lines.length; i++) {
      const line = lines[i]!;
      if (line.trim() === '') continue;
      if (getIndent(line) <= startIndent) {
        return Math.max(startLine, i - 1);
      }
    }
    return lines.length - 1;
  }

  // Brace-based languages
  let depth = 0;
  let foundOpen = false;
  for (let i = startLine; i < lines.length; i++) {
    const line = lines[i]!;
    for (const ch of line) {
      if (ch === '{') { depth++; foundOpen = true; }
      else if (ch === '}') {
        depth--;
        if (foundOpen && depth === 0) return i;
      }
    }
  }

  return Math.min(startLine + 1, lines.length - 1);
}

function getIndent(line: string): number {
  let count = 0;
  for (const ch of line) {
    if (ch === ' ') count++;
    else if (ch === '\t') count += 4;
    else break;
  }
  return count;
}
